import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useCollection, PAGE_SIZE } from '../hooks/useCollection';
import type { CollectionEntry } from '../types/cms';

const COLLECTION_PREFIX = import.meta.env.VITE_COLLECTION_PREFIX ?? 'c';

function EntryListItem({
  entry,
  typeSlug,
  imageField,
}: {
  entry: CollectionEntry;
  typeSlug: string;
  imageField?: string;
}) {
  const data = entry.dataJson ?? {};
  const imageUrl = imageField
    ? (data[imageField] as string | undefined)
    : (data.imageUrl as string | undefined) ??
      (data.image as string | undefined) ??
      (data.cover as string | undefined);
  const excerpt =
    (data.excerpt as string | undefined) ??
    (data.summary as string | undefined) ??
    (data.description as string | undefined);
  const path = `/${COLLECTION_PREFIX}/${encodeURIComponent(typeSlug)}/${encodeURIComponent(entry.slug ?? entry.id)}`;
  return (
    <Link
      to={path}
      className="flex gap-4 p-4 bg-white border border-zinc-100 rounded-xl hover:shadow-sm transition-shadow"
    >
      {imageUrl && (
        <div className="w-20 h-20 shrink-0 overflow-hidden rounded-lg bg-zinc-100">
          <img src={imageUrl} alt={entry.title} className="w-full h-full object-cover" loading="lazy" />
        </div>
      )}
      <div className="flex-1 min-w-0 space-y-1">
        <h3 className="font-semibold text-zinc-900 text-sm leading-snug">{entry.title}</h3>
        {excerpt && <p className="text-xs text-zinc-500 line-clamp-2">{excerpt}</p>}
        {entry.publishedAt && (
          <p className="text-[10px] text-zinc-400">
            {new Date(entry.publishedAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          </p>
        )}
      </div>
    </Link>
  );
}

interface Props { typeSlug?: string; imageField?: string; }

export function CollectionEntriesListBlock({ typeSlug: propSlug, imageField }: Props = {}) {
  const { typeSlug: paramSlug } = useParams<{ typeSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [localPage, setLocalPage] = useState(0);

  const embedded = !!propSlug;
  const typeSlug = propSlug ?? paramSlug;
  const page = embedded
    ? Math.max(0, parseInt(searchParams.get('page') ?? '1', 10) - 1)
    : localPage;

  function setPage(next: number) {
    if (embedded) {
      setSearchParams(next === 0 ? {} : { page: String(next + 1) }, { replace: true });
    } else {
      setLocalPage(next);
    }
  }

  const { data, isLoading } = useCollection(typeSlug, page);
  const entries = data?.entries ?? [];
  const total = data?.total ?? entries.length;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (!typeSlug) return null;
  if (isLoading) return <div className="py-8 text-center text-zinc-400 text-sm">Loading...</div>;
  if (!entries.length) return <div className="py-8 text-center text-zinc-400 text-sm">No entries found.</div>;

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        {entries.map((entry) => (
          <EntryListItem key={entry.id} entry={entry} typeSlug={typeSlug} imageField={imageField} />
        ))}
      </div>
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            className="px-3 py-1 text-sm border border-zinc-200 rounded-lg disabled:opacity-40"
          >
            Previous
          </button>
          <span className="px-3 py-1 text-sm text-zinc-500">{page + 1} / {totalPages}</span>
          <button
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
            className="px-3 py-1 text-sm border border-zinc-200 rounded-lg disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
